"use client";

import { Badge } from "../../";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "../../";
import { cn } from "../../utils";
import {
  IconCircleCheck,
  IconChevronRight,
  IconCircleDot,
  IconCircle,
  IconX,
} from "@tabler/icons-react";
import { type ComponentProps, createContext, type HTMLAttributes, useContext } from "react";

type TestStatusEnum = "passed" | "failed" | "skipped" | "running";

interface ITestResultsSummary {
  passed: number;
  failed: number;
  skipped: number;
  total: number;
  duration?: number;
}

interface TestResultsContextType {
  summary?: ITestResultsSummary;
}

const TestResultsContext = createContext<TestResultsContextType>({});

export type TestResultsProps = HTMLAttributes<HTMLDivElement> & {
  summary?: ITestResultsSummary;
};

export const TestResults = ({ summary, className, children, ...props }: TestResultsProps) => (
  <TestResultsContext.Provider value={{ summary }}>
    <div className={cn("al-rounded-lg al-border al-bg-background", className)} {...props}>
      {children ??
        (summary && (
          <TestResultsHeader>
            <TestResultsSummary />
            <TestResultsDuration />
          </TestResultsHeader>
        ))}
    </div>
  </TestResultsContext.Provider>
);

export type TestResultsHeaderProps = HTMLAttributes<HTMLDivElement>;

export const TestResultsHeader = ({ className, children, ...props }: TestResultsHeaderProps) => (
  <div
    className={cn(
      "al-flex al-items-center al-justify-between al-border-b al-px-4 al-py-3",
      className
    )}
    {...props}
  >
    {children}
  </div>
);

export type TestResultsSummaryProps = HTMLAttributes<HTMLDivElement>;

export const TestResultsSummary = ({ className, children, ...props }: TestResultsSummaryProps) => {
  const { summary } = useContext(TestResultsContext);

  if (!summary) {
    return null;
  }

  return (
    <div className={cn("al-flex al-items-center al-gap-3", className)} {...props}>
      {children ?? (
        <>
          <Badge
            className="al-gap-1 al-bg-green-100 al-text-green-700 dark:al-bg-green-900/30 dark:al-text-green-400"
            variant="secondary"
          >
            <IconCircleCheck className="al-size-3" />
            {summary.passed} passed
          </Badge>
          {summary.failed > 0 && (
            <Badge
              className="al-gap-1 al-bg-red-100 al-text-red-700 dark:al-bg-red-900/30 dark:al-text-red-400"
              variant="secondary"
            >
              <IconX className="al-size-3" />
              {summary.failed} failed
            </Badge>
          )}
          {summary.skipped > 0 && (
            <Badge
              className="al-gap-1 al-bg-yellow-100 al-text-yellow-700 dark:al-bg-yellow-900/30 dark:al-text-yellow-400"
              variant="secondary"
            >
              <IconCircle className="al-size-3" />
              {summary.skipped} skipped
            </Badge>
          )}
        </>
      )}
    </div>
  );
};

export type TestResultsDurationProps = HTMLAttributes<HTMLSpanElement>;

export const TestResultsDuration = ({
  className,
  children,
  ...props
}: TestResultsDurationProps) => {
  const { summary } = useContext(TestResultsContext);

  if (!summary?.duration) {
    return null;
  }

  const formatDuration = (ms: number) => {
    if (ms < 1000) {
      return `${ms}ms`;
    }
    return `${(ms / 1000).toFixed(2)}s`;
  };

  return (
    <span className={cn("al-text-muted-foreground al-text-sm", className)} {...props}>
      {children ?? formatDuration(summary.duration)}
    </span>
  );
};

export type TestResultsProgressProps = HTMLAttributes<HTMLDivElement>;

export const TestResultsProgress = ({
  className,
  children,
  ...props
}: TestResultsProgressProps) => {
  const { summary } = useContext(TestResultsContext);

  if (!summary) {
    return null;
  }

  const passedPercent = (summary.passed / summary.total) * 100;
  const failedPercent = (summary.failed / summary.total) * 100;

  return (
    <div className={cn("al-space-y-2", className)} {...props}>
      {children ?? (
        <>
          <div className="al-flex al-h-2 al-overflow-hidden al-rounded-full al-bg-muted">
            <div
              className="al-bg-green-500 al-transition-all"
              style={{ width: `${passedPercent}%` }}
            />
            <div
              className="al-bg-red-500 al-transition-all"
              style={{ width: `${failedPercent}%` }}
            />
          </div>
          <div className="al-flex al-justify-between al-text-muted-foreground al-text-xs">
            <span>
              {summary.passed}/{summary.total} tests passed
            </span>
            <span>{passedPercent.toFixed(0)}%</span>
          </div>
        </>
      )}
    </div>
  );
};

export type TestResultsContentProps = HTMLAttributes<HTMLDivElement>;

export const TestResultsContent = ({ className, children, ...props }: TestResultsContentProps) => (
  <div className={cn("al-space-y-2 al-p-4", className)} {...props}>
    {children}
  </div>
);

interface TestSuiteContextType {
  name: string;
  status: TestStatusEnum;
}

const TestSuiteContext = createContext<TestSuiteContextType>({
  name: "",
  status: "passed",
});

export type TestSuiteProps = ComponentProps<typeof Collapsible> & {
  name: string;
  status: TestStatusEnum;
};

export const TestSuite = ({ name, status, className, children, ...props }: TestSuiteProps) => (
  <TestSuiteContext.Provider value={{ name, status }}>
    <Collapsible className={cn("al-rounded-lg al-border", className)} {...props}>
      {children}
    </Collapsible>
  </TestSuiteContext.Provider>
);

export type TestSuiteNameProps = ComponentProps<typeof CollapsibleTrigger>;

export const TestSuiteName = ({ className, children, ...props }: TestSuiteNameProps) => {
  const { name, status } = useContext(TestSuiteContext);

  return (
    <CollapsibleTrigger
      className={cn(
        "al-group al-flex al-w-full al-items-center al-gap-2 al-px-4 al-py-3 al-text-left al-transition-colors hover:al-bg-muted/50",
        className
      )}
      {...props}
    >
      <IconChevronRight className="al-size-4 al-shrink-0 al-text-muted-foreground al-transition-transform group-data-[state=open]:al-rotate-90" />
      <TestStatusIcon status={status} />
      <span className="al-font-medium al-text-sm">{children ?? name}</span>
    </CollapsibleTrigger>
  );
};

export type TestSuiteStatsProps = HTMLAttributes<HTMLDivElement> & {
  passed?: number;
  failed?: number;
  skipped?: number;
};

export const TestSuiteStats = ({
  passed = 0,
  failed = 0,
  skipped = 0,
  className,
  children,
  ...props
}: TestSuiteStatsProps) => (
  <div
    className={cn("al-ml-auto al-flex al-items-center al-gap-2 al-text-xs", className)}
    {...props}
  >
    {children ?? (
      <>
        {passed > 0 && (
          <span className="al-text-green-600 dark:al-text-green-400">{passed} passed</span>
        )}
        {failed > 0 && (
          <span className="al-text-red-600 dark:al-text-red-400">{failed} failed</span>
        )}
        {skipped > 0 && (
          <span className="al-text-yellow-600 dark:al-text-yellow-400">{skipped} skipped</span>
        )}
      </>
    )}
  </div>
);

export type TestSuiteContentProps = ComponentProps<typeof CollapsibleContent>;

export const TestSuiteContent = ({ className, children, ...props }: TestSuiteContentProps) => (
  <CollapsibleContent className={cn("al-border-t", className)} {...props}>
    <div className="al-divide-y">{children}</div>
  </CollapsibleContent>
);

interface TestContextType {
  name: string;
  status: TestStatusEnum;
  duration?: number;
}

const TestContext = createContext<TestContextType>({
  name: "",
  status: "passed",
});

export type TestProps = HTMLAttributes<HTMLDivElement> & {
  name: string;
  status: TestStatusEnum;
  duration?: number;
};

export const Test = ({ name, status, duration, className, children, ...props }: TestProps) => (
  <TestContext.Provider value={{ name, status, duration }}>
    <div
      className={cn("al-flex al-items-center al-gap-2 al-px-4 al-py-2 al-text-sm", className)}
      {...props}
    >
      {children ?? (
        <>
          <TestStatus />
          <TestName />
          {duration !== undefined && <TestDuration />}
        </>
      )}
    </div>
  </TestContext.Provider>
);

const statusStyles: Record<TestStatusEnum, string> = {
  passed: "text-green-600 dark:text-green-400",
  failed: "text-red-600 dark:text-red-400",
  skipped: "text-yellow-600 dark:text-yellow-400",
  running: "text-blue-600 dark:text-blue-400",
};

const statusIcons: Record<TestStatusEnum, React.ReactNode> = {
  passed: <IconCircleCheck className="al-size-4" />,
  failed: <IconX className="al-size-4" />,
  skipped: <IconCircle className="al-size-4" />,
  running: <IconCircleDot className="al-size-4 al-animate-pulse" />,
};

const TestStatusIcon = ({ status }: { status: TestStatusEnum }) => (
  <span className={cn("al-shrink-0", statusStyles[status])}>{statusIcons[status]}</span>
);

export type TestStatusProps = HTMLAttributes<HTMLSpanElement>;

export const TestStatus = ({ className, children, ...props }: TestStatusProps) => {
  const { status } = useContext(TestContext);

  return (
    <span className={cn("al-shrink-0", statusStyles[status], className)} {...props}>
      {children ?? statusIcons[status]}
    </span>
  );
};

export type TestNameProps = HTMLAttributes<HTMLSpanElement>;

export const TestName = ({ className, children, ...props }: TestNameProps) => {
  const { name } = useContext(TestContext);

  return (
    <span className={cn("al-flex-1", className)} {...props}>
      {children ?? name}
    </span>
  );
};

export type TestDurationProps = HTMLAttributes<HTMLSpanElement>;

export const TestDuration = ({ className, children, ...props }: TestDurationProps) => {
  const { duration } = useContext(TestContext);

  if (duration === undefined) {
    return null;
  }

  return (
    <span className={cn("al-ml-auto al-text-muted-foreground al-text-xs", className)} {...props}>
      {children ?? `${duration}ms`}
    </span>
  );
};

export type TestErrorProps = HTMLAttributes<HTMLDivElement>;

export const TestError = ({ className, children, ...props }: TestErrorProps) => (
  <div
    className={cn("al-mt-2 al-rounded-md al-bg-red-50 al-p-3 dark:al-bg-red-900/20", className)}
    {...props}
  >
    {children}
  </div>
);

export type TestErrorMessageProps = HTMLAttributes<HTMLParagraphElement>;

export const TestErrorMessage = ({ className, children, ...props }: TestErrorMessageProps) => (
  <p
    className={cn("al-font-medium al-text-red-700 al-text-sm dark:al-text-red-400", className)}
    {...props}
  >
    {children}
  </p>
);

export type TestErrorStackProps = HTMLAttributes<HTMLPreElement>;

export const TestErrorStack = ({ className, children, ...props }: TestErrorStackProps) => (
  <pre
    className={cn(
      "al-mt-2 al-overflow-auto al-font-mono al-text-red-600 al-text-xs dark:al-text-red-400",
      className
    )}
    {...props}
  >
    {children}
  </pre>
);
